import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  House, MagicWand, User, SignOut, Bell, CaretLeft, CaretRight,
  ChartBar, ShieldCheck, RssSimple, Translate, Question,
  Article, Dna, Images, Plugs, Sparkle, CreditCard
} from '@phosphor-icons/react';


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

function LangSwitcher({ collapsed }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div className="relative">
      <button
        className="sidebar-nav-item w-full text-[var(--text-muted)]"
        onClick={() => setOpen(o => !o)}
        title={collapsed ? current.flag : ''}
        style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
      >
        <Translate size={18} />
        {!collapsed && <span className="text-sm flex-1 text-left">{current.flag} {current.label}</span>}
      </button>
      {open && (
        <div
          className="absolute z-50 rounded-lg shadow-xl border border-[var(--border)] overflow-hidden"
          style={{
            background: 'var(--bg-card)',
            bottom: '100%',
            left: collapsed ? '100%' : 0,
            marginBottom: 4,
            minWidth: 160,
          }}
        >
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-hover)] transition-colors flex items-center gap-2"
              style={{ color: lang.code === i18n.language ? 'var(--accent-purple)' : 'var(--text-primary)' }}
              onClick={() => { i18n.changeLanguage(lang.code); setOpen(false); }}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ activeView, setActiveView, isProjectView, onHelpOpen, selectedProject }) {
  const { user, logout, api, planLimits, refreshPlanLimits, effectivePlan } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  // SOCIAL-RECONNECT-REMINDER-001: true only once the one-time reminder
  // modal has already been shown for this project AND it still has zero
  // connected social accounts — self-resolves the moment one gets
  // connected, no separate "dismissed" flag needed. Purely informational,
  // never blocks anything; it just lights up the Connections nav item and
  // the bell so the user remembers publishing needs a connected account.
  const [projectHasNoSocial, setProjectHasNoSocial] = useState(false);

  useEffect(() => {
    api.get('/notifications/unread-count').then(r => setUnread(r.data.unread)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!isProjectView || !selectedProject?.id || !selectedProject?.social_reminder_shown) {
      setProjectHasNoSocial(false);
      return;
    }
    let cancelled = false;
    api.get(`/social/project/${selectedProject.id}`)
      .then(r => { if (!cancelled) setProjectHasNoSocial((r.data || []).length === 0); })
      .catch(() => {});
    return () => { cancelled = true; };
    // Re-checks on every tab switch inside the project too (not just when
    // selectedProject itself changes) — cheap GET, and it's the simplest
    // way for the dot to clear itself right after the user connects an
    // account from the Connections tab and switches away, without a more
    // invasive cross-component refresh mechanism.
  }, [isProjectView, selectedProject, activeView, api]);

  // IDLE-API-POLLING-20261030: the former 30s unconditional interval
  // generated thousands of /api/plan/limits traffic events from idle tabs.
  // AuthContext already loads this snapshot on login. Keep it fresh while
  // visible, and refresh on return only when the snapshot is stale.
  useEffect(() => {
    const pollEveryMs = 5 * 60 * 1000;
    const minRefreshGapMs = 60 * 1000;
    let lastRefreshAt = Date.now();

    const refreshIfVisibleAndStale = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastRefreshAt < minRefreshGapMs) return;
      lastRefreshAt = now;
      refreshPlanLimits();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfVisibleAndStale();
    };
    const interval = window.setInterval(refreshIfVisibleAndStale, pollEveryMs);
    window.addEventListener('focus', refreshIfVisibleAndStale);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisibleAndStale);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refreshPlanLimits]);

  const isAdmin = user?.role === 'admin';
  const resolvedPlan = String(effectivePlan || user?.plan || 'free').toLowerCase();
  const canUseDesignDna = isAdmin || ['strategist', 'custom', 'admin'].includes(resolvedPlan);
  const canAnalytics = Boolean(planLimits?.can_analytics || isAdmin || ['osmel@osmelfabre.it', 'osmel.fabre@gmail.com'].includes(user?.email || ''));
  // FEED-STANDBY-001: RSS Feed nav entry only shown to internal testers —
  // PRODUCT-IA-CLEANUP-001A (task section 1): this stays a separate,
  // conditionally-visible extra, deliberately never folded into the five
  // primary project destinations below.
  const isInternalFeedTester = isAdmin || ['osmel@osmelfabre.it', 'osmel.fabre@gmail.com'].includes(user?.email || '');
  const aiUsage = planLimits?.ai_usage;
  const aiPercent = aiUsage?.unlimited || !aiUsage?.limit
    ? 0
    : Math.min(100, Math.round(((aiUsage?.used || 0) / aiUsage.limit) * 100));
  // FREE-PLAN-WALLET-PUBLISHING-001A: Free's monthly limit is 0 by
  // design — its usable credits come from the one-time starter grant,
  // so the real balance is total_remaining, not the monthly-only field.
  const aiTotalRemaining = aiUsage?.total_remaining ?? aiUsage?.remaining ?? 0;
  const creditColor = aiPercent >= 90
    ? 'linear-gradient(90deg, #f43f5e, #ec4899)'
    : aiPercent >= 75
      ? 'linear-gradient(90deg, #f59e0b, #f97316)'
      : 'linear-gradient(90deg, #7c5cff, #e646aa)';
  const w = collapsed ? 68 : 192;

  // PRODUCT-IA-CLEANUP-001A (task section 1): the five primary project
  // destinations, replacing the old Calendar/Personas/Creative/Design/
  // Social entries — each now lives as contextual navigation INSIDE its
  // consolidated workspace (Content, DNA, Connections) instead of as a
  // separate top-level Sidebar item. Shared between desktop and mobile so
  // both surfaces stay structurally identical (task section 1: "Do the
  // equivalent cleanup in mobile navigation").
  const projectNavItems = [
    { id: 'content', icon: Article, label: t('nav.content') },
    { id: 'dna', icon: Dna, label: canUseDesignDna ? t('nav.dna') : t('nav.brandWorkspace') },
    { id: 'media', icon: Images, label: t('nav.media') },
    { id: 'connections', icon: Plugs, label: t('nav.connections') },
    ...(canAnalytics ? [{ id: 'analytics', icon: ChartBar, label: t('nav.analytics') }] : []),
    // FEED-STANDBY-001: RSS Feed is on standby, out of the primary product
    // flow — only shown to internal testers (admin role or an explicit
    // internal email), and labeled Beta when it is.
    ...(isInternalFeedTester ? [{ id: 'feeds', icon: RssSimple, label: `${t('nav.feeds')} (${t('project.feeds.beta')})` }] : []),
  ];

  if (isMobile) {
    // PRODUCT-IA-CLEANUP-001B (task section 3): the global destinations
    // (Dashboard, New Project) must stay consistent with desktop's
    // always-visible mainNav regardless of isProjectView — mobile
    // previously dropped New Project entirely while inside a project,
    // making it unreachable except by first leaving the project view.
    // Horizontal scrolling is acceptable if this list overflows the
    // viewport width; hiding New Project is not.
    const mobileItems = isProjectView
      ? [
          { id: 'dashboard', icon: House, label: t('nav.dashboard') },
          { id: 'wizard', icon: MagicWand, label: t('nav.newProject') },
          ...projectNavItems,
        ]
      : [
          { id: 'dashboard', icon: House, label: t('nav.dashboard') },
          { id: 'wizard', icon: MagicWand, label: t('nav.newProject') },
          { id: 'notifications', icon: Bell, label: t('nav.notifications') },
          ...(isAdmin ? [{ id: 'admin', icon: ShieldCheck, label: t('nav.admin') }] : []),
          // EFFECTIVE-PLAN-SESSION-ONBOARDING-001: Billing must remain
          // voluntarily reachable — it's no longer force-opened for Free
          // users, so it needs an explicit, always-visible entry point.
          { id: 'billing', icon: CreditCard, label: t('nav.billing') },
          { id: 'profile', icon: User, label: t('nav.profile') },
        ];

    return (
      <div className="sidebar relative" data-testid="mobile-bottom-nav">
        {!aiUsage?.unlimited && aiUsage?.limit > 0 && (
          <div className="absolute left-0 right-0 top-0 h-[3px] bg-white/5" title={t('profile.aiCreditsUsed', { used: aiUsage.used || 0 })}>
            <div className="h-full transition-all duration-500" style={{ width: `${aiPercent}%`, background: creditColor }} />
          </div>
        )}
        <nav>
          {mobileItems.map(item => (
            <div
              key={item.id}
              data-testid={`nav-${item.id}`}
              className={`sidebar-nav-item relative ${activeView === item.id ? 'active' : ''}`}
              onClick={() => {
                if (item.id === 'notifications') {
                  setActiveView('notifications');
                  api.post('/notifications/mark-read').then(() => setUnread(0)).catch(() => {});
                } else {
                  setActiveView(item.id);
                }
              }}
            >
              <div className="relative">
                <item.icon weight={activeView === item.id ? 'fill' : 'regular'} size={20} />
                {item.id === 'notifications' && unread > 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--accent-pink)] text-[7px] font-bold flex items-center justify-center">{unread}</span>
                )}
                {item.id === 'connections' && projectHasNoSocial && (
                  <span data-testid="connections-nosocial-dot" className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--accent-pink)]" />
                )}
              </div>
              <span>{item.label}</span>
            </div>
          ))}
        </nav>
      </div>
    );
  }

  const mainNav = [
    { id: 'dashboard', icon: House, label: t('nav.dashboard') },
    { id: 'wizard', icon: MagicWand, label: t('nav.newProject') },
  ];

  return (
    <div className="sidebar flex flex-col" data-collapsed={collapsed} style={{ width: w, minWidth: w, padding: collapsed ? '1rem 0.5rem' : '1rem', transition: 'width 0.2s ease, min-width 0.2s ease, padding 0.2s ease' }}>
      {/* Logo + Collapse */}
      <div className="flex items-center gap-2 mb-6" style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}>
        {collapsed
          ? <img src="/assets/favicon.jpg" alt="Sketchario" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
          : <img src="/assets/LOGO-sketchario.jpg" alt="Sketchario" style={{ height: 32, maxWidth: '125px', objectFit: 'contain', flexShrink: 0 }} />
        }
        {!collapsed && (
          <button data-testid="sidebar-toggle" className="ml-auto p-1 rounded hover:bg-[var(--bg-card)] transition-colors" onClick={() => setCollapsed(true)}>
            <CaretLeft size={14} />
          </button>
        )}
      </div>
      {collapsed && (
        <button data-testid="sidebar-toggle" className="flex items-center justify-center w-full mb-2 p-1 rounded hover:bg-[var(--bg-card)] transition-colors" onClick={() => setCollapsed(false)}>
          <CaretRight size={14} />
        </button>
      )}

      {!collapsed && <div className="workspace-identity"><span>{t('workspace.workspaceLabel')}</span><strong>{user?.name || user?.email}</strong></div>}
      {/* Main Nav */}
      <nav className="space-y-1 mb-3">
        {mainNav.map(item => (
          <div
            key={item.id}
            data-testid={`nav-${item.id}`}
            className={`sidebar-nav-item relative ${activeView === item.id ? 'active' : ''}`}
            onClick={() => setActiveView(item.id)}
            title={collapsed ? item.label : ''}
            style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
          >
            <item.icon weight={activeView === item.id ? 'fill' : 'regular'} size={20} />
            {!collapsed && <span>{item.label}</span>}
          </div>
        ))}
      </nav>

      {/* Project Nav */}
      {isProjectView && (
        <div
          className="rounded-2xl p-2 mb-3 space-y-1 border"
          style={{
            background: 'var(--bg-card)',
            borderColor: 'rgba(124,92,255,0.18)',
            boxShadow: 'none',
          }}
        >
          {!collapsed && (
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase px-2 mb-2 tracking-[0.18em]">
              {t('nav.project')}
            </p>
          )}
          {projectNavItems.map(item => (
            <div
              key={item.id}
              data-testid={`nav-${item.id}`}
              className={`sidebar-nav-item relative ${activeView === item.id ? 'active' : ''}`}
              onClick={() => setActiveView(item.id)}
              title={collapsed ? item.label : ''}
              style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
            >
              <div className="relative">
                <item.icon weight={activeView === item.id ? 'fill' : 'regular'} size={18} />
                {item.id === 'connections' && projectHasNoSocial && (
                  <span data-testid="connections-nosocial-dot" className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--accent-pink)]" />
                )}
              </div>
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Admin */}
      {isAdmin && (
        <div
          data-testid="nav-admin"
          className={`sidebar-nav-item relative ${activeView === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveView('admin')}
          title={collapsed ? t('nav.admin') : ''}
          style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
        >
          <ShieldCheck weight={activeView === 'admin' ? 'fill' : 'regular'} size={20} />
          {!collapsed && <span>{t('nav.admin')}</span>}
        </div>
      )}

      {/* EFFECTIVE-PLAN-SESSION-ONBOARDING-001: Billing must remain
          voluntarily reachable — it's no longer force-opened for Free
          users, so it needs an explicit, always-visible entry point. */}
      <div
        data-testid="nav-billing"
        className={`sidebar-nav-item relative ${activeView === 'billing' ? 'active' : ''}`}
        onClick={() => setActiveView('billing')}
        title={collapsed ? t('nav.billing') : ''}
        style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
      >
        <CreditCard weight={activeView === 'billing' ? 'fill' : 'regular'} size={20} />
        {!collapsed && <span>{t('nav.billing')}</span>}
      </div>

      {aiUsage && (
        <button
          className="mb-3 rounded-2xl border text-left transition-colors hover:border-[rgba(124,92,255,0.45)]"
          style={{
            padding: collapsed ? '0.55rem' : '0.75rem',
            borderColor: 'rgba(124,92,255,0.2)',
            background: 'var(--bg-card)',
          }}
          onClick={() => setActiveView('profile')}
          title={collapsed ? t('profile.aiCredits') : ''}
          data-testid="sidebar-ai-credits"
        >
          {collapsed ? (
            <div className="relative mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-white/5">
              <Sparkle size={15} />
              {!aiUsage.unlimited && (
                <span
                  className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full border-2 border-[var(--bg-secondary)]"
                  style={{ background: aiPercent >= 90 ? '#f43f5e' : aiPercent >= 75 ? '#f59e0b' : '#7c5cff' }}
                />
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold">
                  <Sparkle size={14} /> {t('profile.aiCredits')}
                </span>
                <span className="text-[10px] text-[var(--text-muted)]">
                  {aiUsage.unlimited
                    ? t('profile.unlimited')
                    : aiUsage.limit
                      ? `${aiUsage.remaining ?? 0}/${aiUsage.limit ?? 0}`
                      : `${aiTotalRemaining}`}
                </span>
              </div>
              {!aiUsage.unlimited && (
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${aiPercent}%`, background: creditColor }} />
                </div>
              )}
            </>
          )}
        </button>
      )}

      <div className="flex-1" />

      {/* Help */}
      <button
        data-testid="help-btn"
        className="sidebar-nav-item text-[var(--text-muted)]"
        onClick={onHelpOpen}
        title={collapsed ? 'Guida' : ''}
        style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
      >
        <Question size={20} />
        {!collapsed && <span>Guida</span>}
      </button>

      {/* Notifications bell */}
      <button
        data-testid="notifications-bell"
        className="sidebar-nav-item relative"
        onClick={() => { setActiveView('notifications'); api.post('/notifications/mark-read').then(() => setUnread(0)).catch(() => {}); }}
        style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
      >
        <div className="relative">
          <Bell size={20} weight={(unread > 0 || projectHasNoSocial) ? 'fill' : 'regular'} />
          {unread > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[var(--accent-pink)] text-[8px] font-bold flex items-center justify-center">{unread}</span>}
          {unread === 0 && projectHasNoSocial && (
            <span data-testid="bell-nosocial-dot" className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[var(--accent-pink)] border-2 border-[var(--bg-secondary)]" />
          )}
        </div>
        {!collapsed && <span>{t('nav.notifications')}</span>}
      </button>

      {/* Language switcher */}
      <LangSwitcher collapsed={collapsed} />

      {/* User Profile */}
      <div
        data-testid="nav-profile"
        className="user-profile-card cursor-pointer hover:border-[var(--gradient-start)] transition-colors mt-2"
        onClick={() => setActiveView('profile')}
        style={collapsed ? { justifyContent: 'center', padding: '0.5rem', flexDirection: 'column', alignItems: 'center', gap: 0 } : {}}
      >
        <div className="user-avatar" style={{ width: collapsed ? 28 : 32, height: collapsed ? 28 : 32, flexShrink: 0 }}>
          <User weight="fill" size={collapsed ? 12 : 14} color="white" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs truncate">{user?.name || 'Utente'}</p>
            <p className="text-[10px] text-[var(--text-muted)] capitalize">{effectivePlan || user?.plan || 'free'}</p>
          </div>
        )}
      </div>

      {/* Logout */}
      <button
        data-testid="logout-btn"
        className="sidebar-nav-item mt-2 text-[var(--text-muted)]"
        onClick={logout}
        title={collapsed ? t('nav.logout') : ''}
        style={collapsed ? { justifyContent: 'center', padding: '0.75rem' } : {}}
      >
        <SignOut size={18} />
        {!collapsed && <span className="text-sm">{t('nav.logout')}</span>}
      </button>
    </div>
  );
}

